import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronsUpDown, ChevronUp, ChevronDown, Info } from "lucide-react";
import { formatAmount } from "@/utils/formatAmount";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { InvoiceAiBadge, type InvoiceAiAnalysis } from "@/features/invoices/components/InvoiceAiBadge";
import { displayAmount, isMandateInvoice, netTotals } from "@/features/invoices/invoiceTotals";
import {
  EMPTY_PGK_FILTERS,
  NO_PROJECT,
  applyPgkFilters,
  distinctOptions,
  hasActivePgkFilters,
  loadStoredPgkFilters,
  pgkFiltersStorageKey,
  pgkMonthlyStats,
  pgkMonthlyTotals,
  pgkSheetCalcStorageKey,
  type PgkFilters,
} from "@/features/invoices/invoiceFilters";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { FilterMultiSelect, type FilterOption } from "@/components/ui/FilterMultiSelect";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { PageLoader } from "@/components/ui/Spinner";
import {
  SheetCalcProvider,
  SheetCalcToggleButton,
  CalcCell,
} from "@/features/finances/components/SheetCalc";
import { useAuthStore } from "@/store/auth";

interface Invoice {
  id: string;
  invoice_number: string;
  order_number: string;
  order_id: string;
  contractor_name: string;
  submitted_by_name: string;
  amount_net: string | null;
  amount_gross: string;
  /** "" for companies; "uz" / "uod" for natural-person settlements. */
  mandate_type?: "" | "uz" | "uod";
  /** "Faktura" / "Rachunek" / "Umowa zlecenie" / "Umowa o dzieło". */
  document_label?: string;
  /** Project of the invoice's order — resolved server-side through BOTH roads. */
  project_id?: string | null;
  project_name?: string;
  status: string;
  booking_status: string;
  payment_status: string;
  paid_amount: string;
  status_comment: string;
  payment_comment: string;
  submitted_at: string;
  ai_analysis?: InvoiceAiAnalysis | null;
}

type SortKey = "invoice_number" | "order_number" | "contractor_name" | "submitted_by_name" | "amount_gross" | "status" | "booking_status" | "payment_status";
type SortDir = "asc" | "desc";

interface InvoiceEvent {
  id: string;
  event_type: string;
  actor_name: string;
  old_value: string;
  new_value: string;
  comment: string;
  timestamp: string;
}

const statusColor: Record<string, "gray" | "green" | "red" | "blue"> = {
  submitted: "blue",
  accepted: "green",
  rejected: "red",
  withdrawn: "red",
};
const statusLabel: Record<string, string> = {
  submitted: "Przesłana",
  accepted: "Przyjęta",
  rejected: "Nieprzyjęta",
  withdrawn: "Wycofana",
};
const paymentColor: Record<string, "gray" | "green" | "yellow" | "blue"> = {
  unpaid: "gray",
  partially_paid: "yellow",
  transfer_prepared: "blue",
  paid: "green",
};
const paymentLabel: Record<string, string> = {
  unpaid: "Nieopłacona",
  partially_paid: "Opłacona częściowo",
  transfer_prepared: "Przelew przygotowany",
  paid: "Opłacona",
};

const STATUS_OPTIONS: FilterOption[] = [
  { value: "submitted", label: "Przesłana" },
  { value: "accepted", label: "Przyjęta" },
  { value: "rejected", label: "Nieprzyjęta" },
  { value: "withdrawn", label: "Wycofana" },
];
const BOOKING_OPTIONS: FilterOption[] = [
  { value: "not_booked", label: "Niezaksięgowana" },
  { value: "booked", label: "Zaksięgowana" },
];
const PAYMENT_OPTIONS: FilterOption[] = [
  { value: "unpaid", label: "Nieopłacona" },
  { value: "partially_paid", label: "Opłacona częściowo" },
  { value: "transfer_prepared", label: "Przelew przygotowany" },
  { value: "paid", label: "Opłacona" },
];

const eventValueLabel: Record<string, string> = {
  submitted: "Przesłana",
  accepted: "Przyjęta",
  rejected: "Nieprzyjęta",
  withdrawn: "Wycofana",
  not_booked: "Niezaksięgowana",
  booked: "Zaksięgowana",
  unpaid: "Nieopłacona",
  partially_paid: "Opłacona częściowo",
  transfer_prepared: "Przelew przygotowany",
  paid: "Opłacona",
};

const eventTypeLabel: Record<string, string> = {
  submitted: "Przesłanie faktury",
  status_changed: "Zmiana statusu",
  booking_changed: "Zmiana zaksięgowania",
  payment_added: "Dodanie płatności",
  payment_deleted: "Usunięcie płatności",
};

const eventTypeColor: Record<string, string> = {
  submitted: "bg-blue-500",
  status_changed: "bg-violet-500",
  booking_changed: "bg-amber-500",
  payment_added: "bg-green-500",
  payment_deleted: "bg-red-400",
};

function formatTs(ts: string) {
  return new Date(ts).toLocaleString("pl-PL", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

interface TodoBadge {
  label: string;
  className: string;
}

function getActionBadges(inv: Invoice, role?: string): TodoBadge[] {
  const badges: TodoBadge[] = [];

  if (role === "director" && inv.status === "submitted") {
    badges.push({
      label: "Zaakceptuj / Odrzuć",
      className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800",
    });
  }

  if (inv.status === "accepted") {
    if (inv.booking_status !== "booked") {
      badges.push({
        label: "Zaksięguj",
        className: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800",
      });
    }
    if (inv.payment_status === "unpaid") {
      badges.push({
        label: "Nieopłacona",
        className: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800",
      });
    } else if (inv.payment_status === "partially_paid") {
      badges.push({
        label: "Cz. opłacona",
        className: "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200 dark:border-orange-800",
      });
    } else if (inv.payment_status === "transfer_prepared") {
      badges.push({
        label: "Przelew przyg.",
        className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800",
      });
    }
  }

  return badges;
}

function sortWithdrawnLast(list: Invoice[]): Invoice[] {
  return [...list].sort((a, b) => {
    if (a.status === "withdrawn" && b.status !== "withdrawn") return 1;
    if (a.status !== "withdrawn" && b.status === "withdrawn") return -1;
    return 0;
  });
}

function applySort(list: Invoice[], key: SortKey | null, dir: SortDir): Invoice[] {
  if (!key) return sortWithdrawnLast(list);
  return [...list].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (key === "amount_gross") {
      // Sort by what the column shows: net for companies, gross for UZ/UoD.
      const diff = parseFloat(displayAmount(a)) - parseFloat(displayAmount(b));
      return dir === "asc" ? diff : -diff;
    }
    const cmp = (av ?? "").localeCompare(bv ?? "", "pl");
    return dir === "asc" ? cmp : -cmp;
  });
}

export function AccountingInvoicesPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [selected, setSelected] = useState<Invoice | null>(null);
  const [modalType, setModalType] = useState<"status" | "booking" | null>(null);
  const [form, setForm] = useState({ status: "", booking_status: "", comment: "" });
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [historyInvoiceId, setHistoryInvoiceId] = useState<string | null>(null);

  const filtersKey = pgkFiltersStorageKey(user?.id);
  const [filters, setFilters] = useState<PgkFilters>(() => loadStoredPgkFilters(filtersKey));

  useEffect(() => {
    try {
      localStorage.setItem(filtersKey, JSON.stringify(filters));
    } catch {
      // Storage unavailable — filters simply won't survive a reload.
    }
  }, [filtersKey, filters]);

  const setF = <K extends keyof PgkFilters>(key: K, val: PgkFilters[K]) =>
    setFilters((f) => ({ ...f, [key]: val }));

  function handleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function SortIcon({ col }: { col: SortKey }) {
    if (sortKey !== col) return <ChevronsUpDown className="inline-block ml-1 w-3 h-3 opacity-40" />;
    return sortDir === "asc"
      ? <ChevronUp className="inline-block ml-1 w-3 h-3" />
      : <ChevronDown className="inline-block ml-1 w-3 h-3" />;
  }

  const { data, isLoading } = useQuery({
    queryKey: ["invoices"],
    queryFn: () =>
      apiClient.get<{ data: Invoice[] }>("/invoices/").then((r) => r.data.data),
  });

  const allInvoices = useMemo(() => data ?? [], [data]);

  // Filter options always come from the WHOLE list, so a filter can always be widened
  // again — never from the already-filtered rows.
  const projectOptions = useMemo(() => {
    const opts = distinctOptions(
      allInvoices,
      (inv) => inv.project_id,
      (inv) => inv.project_name || "—",
    );
    if (allInvoices.some((inv) => !inv.project_id))
      opts.push({ value: NO_PROJECT, label: "Bez przedsięwzięcia" });
    return opts;
  }, [allInvoices]);
  const contractorOptions = useMemo(
    () => distinctOptions(allInvoices, (inv) => inv.contractor_name),
    [allInvoices],
  );
  const documentTypeOptions = useMemo(
    () => distinctOptions(allInvoices, (inv) => inv.document_label),
    [allInvoices],
  );
  const authorOptions = useMemo(
    () => distinctOptions(allInvoices, (inv) => inv.submitted_by_name),
    [allInvoices],
  );

  const filtered = useMemo(() => applyPgkFilters(allInvoices, filters), [allInvoices, filters]);
  const sorted = useMemo(() => applySort(filtered, sortKey, sortDir), [filtered, sortKey, sortDir]);
  const totals = useMemo(() => netTotals(filtered), [filtered]);
  const monthlyStats = useMemo(() => pgkMonthlyStats(filtered), [filtered]);
  const monthlyTotals = useMemo(() => pgkMonthlyTotals(monthlyStats), [monthlyStats]);
  const filtersActive = hasActivePgkFilters(filters);

  const { data: historyData, isLoading: historyLoading, isFetching: historyFetching, isError: historyError } = useQuery({
    queryKey: ["invoice-history", historyInvoiceId],
    queryFn: () =>
      apiClient
        .get<{ data: InvoiceEvent[] }>(`/invoices/${historyInvoiceId}/history/`)
        .then((r) => r.data.data),
    enabled: !!historyInvoiceId,
    staleTime: 0,
    gcTime: 0,
  });

  const historyInvoice = historyInvoiceId
    ? allInvoices.find((inv) => inv.id === historyInvoiceId)
    : null;

  const mutation = useMutation({
    mutationFn: ({ action, payload }: { action: string; payload: unknown }) =>
      apiClient.patch(`/invoices/${selected?.id}/${action}/`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.removeQueries({ queryKey: ["invoice-history", selected?.id] });
      setModalType(null);
    },
  });

  const openModal = (inv: Invoice, type: "status" | "booking") => {
    setSelected(inv);
    setModalType(type);
    setForm({
      status: inv.status,
      booking_status: inv.booking_status,
      comment: "",
    });
  };

  return (
    <SheetCalcProvider storageKey={pgkSheetCalcStorageKey(user?.id)}>
    <div>
      <div className="flex items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Faktury P-GK
          </h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-4" />
        </div>
        <div className="flex items-center gap-2">
          <SheetCalcToggleButton size="sm" />
          <button
            className="text-xs px-2.5 sm:px-3 py-1.5 rounded-md font-medium bg-red-600 hover:bg-red-700 text-white transition-colors whitespace-nowrap"
            onClick={() => setFilters(EMPTY_PGK_FILTERS)}
          >
            Wyczyść filtry
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 mb-4 sm:mb-5 flex flex-col gap-2">
        <div className="flex flex-wrap gap-2 items-center">
          <FilterMultiSelect
            label="Przedsięwzięcia"
            options={projectOptions}
            selected={filters.projects}
            onChange={(v) => setF("projects", v)}
          />
          <FilterMultiSelect
            label="Podwykonawcy"
            options={contractorOptions}
            selected={filters.contractors}
            onChange={(v) => setF("contractors", v)}
          />
          <FilterMultiSelect
            label="Typ dokumentu"
            options={documentTypeOptions}
            selected={filters.documentTypes}
            onChange={(v) => setF("documentTypes", v)}
          />
          <FilterMultiSelect
            label="Status FV"
            options={STATUS_OPTIONS}
            selected={filters.statuses}
            onChange={(v) => setF("statuses", v)}
          />
          <FilterMultiSelect
            label="Zaksięgowanie"
            options={BOOKING_OPTIONS}
            selected={filters.bookingStatuses}
            onChange={(v) => setF("bookingStatuses", v)}
          />
          <FilterMultiSelect
            label="Opłacenie"
            options={PAYMENT_OPTIONS}
            selected={filters.paymentStatuses}
            onChange={(v) => setF("paymentStatuses", v)}
          />
          <FilterMultiSelect
            label="Autor"
            options={authorOptions}
            selected={filters.authors}
            onChange={(v) => setF("authors", v)}
          />
        </div>
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3">
          <input
            type="text"
            value={filters.search}
            onChange={(e) => setF("search", e.target.value)}
            placeholder="Szukaj nr faktury lub zamówienia…"
            aria-label="Szukaj po numerze faktury lub zamówienia"
            className="w-full sm:w-64 border border-gray-300 dark:border-gray-600 rounded px-2.5 py-1.5 text-xs bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
          />
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-1.5">
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
              Przesłana:
            </span>
            <div className="flex items-center gap-1.5">
              <DatePicker
                value={filters.submittedFrom}
                onChange={(v) => setF("submittedFrom", v)}
                aria-label="Przesłana od"
                className="flex-1 sm:flex-none"
              />
              <span className="text-xs text-gray-400">—</span>
              <DatePicker
                value={filters.submittedTo}
                onChange={(v) => setF("submittedTo", v)}
                aria-label="Przesłana do"
                className="flex-1 sm:flex-none"
              />
            </div>
          </div>
          {!isLoading && (
            <span className="text-xs text-gray-500 dark:text-gray-400 sm:ml-auto whitespace-nowrap">
              Wybrano:{" "}
              <b className="text-gray-700 dark:text-gray-200">
                {filtered.length.toLocaleString("pl-PL")}
              </b>{" "}
              z {allInvoices.length.toLocaleString("pl-PL")} faktur
            </span>
          )}
        </div>
      </div>

      {data && (
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div
            className="bg-white dark:bg-gray-800 px-4 py-3"
            title="Kafelki liczą wyfiltrowane faktury, pomijając nieprzyjęte i wycofane"
          >
            <div className="text-xs uppercase tracking-wide text-gray-400">Faktur</div>
            <div className="text-lg font-bold tabular-nums text-gray-800 dark:text-gray-100">
              {totals.count.toLocaleString("pl-PL")}
              {totals.count !== filtered.length && (
                <span className="ml-1 text-xs font-normal text-gray-400">
                  z {filtered.length.toLocaleString("pl-PL")}
                </span>
              )}
            </div>
          </div>
          <div
            className="bg-white dark:bg-gray-800 px-4 py-3"
            title="Bez faktur nieprzyjętych i wycofanych. Jak kolumna Kwota: netto dla spółek, rozliczenia UZ/UoD po kwocie umowy"
          >
            <div className="text-xs uppercase tracking-wide text-gray-400">Wartość netto</div>
            <div className="text-lg font-bold tabular-nums text-gray-800 dark:text-gray-100">
              {formatAmount(totals.value)}
            </div>
          </div>
          <div
            className="bg-white dark:bg-gray-800 px-4 py-3"
            title="Bez faktur nieprzyjętych i wycofanych. Wpłaty są brutto — część netto liczona proporcjonalnie do opłacenia faktury"
          >
            <div className="text-xs uppercase tracking-wide text-gray-400">Zapłacono netto</div>
            <div className="text-lg font-bold tabular-nums text-green-700 dark:text-green-400">
              {formatAmount(totals.paid)}
            </div>
          </div>
          <div
            className="bg-white dark:bg-gray-800 px-4 py-3"
            title="Bez faktur nieprzyjętych i wycofanych. Nieopłacona część wartości netto — uwzględnia płatności częściowe"
          >
            <div className="text-xs uppercase tracking-wide text-gray-400">
              Pozostało do zapłaty netto
            </div>
            <div className="text-lg font-bold tabular-nums text-[var(--color-primary)]">
              {formatAmount(totals.outstanding)}
            </div>
          </div>
        </div>
      )}
      {isLoading ? (
        <PageLoader />
      ) : (
        <>
          {/* Mobile card list */}
          <div className="sm:hidden flex flex-col divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {sorted.map((inv) => {
              const isWithdrawn = inv.status === "withdrawn";
              return (
                <div
                  key={inv.id}
                  className={`px-4 py-3 ${isWithdrawn ? "opacity-70 bg-red-50 dark:bg-red-900/10" : ""}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/invoices/${inv.id}`}
                        className={`font-medium hover:underline block ${isWithdrawn ? "line-through text-red-400" : "text-[var(--color-primary)]"}`}
                      >
                        {inv.invoice_number}
                      </Link>
                      <InvoiceAiBadge analysis={inv.ai_analysis} />
                      <Link
                        to={`/orders/${inv.order_id}`}
                        className={`text-xs mt-0.5 block hover:underline ${isWithdrawn ? "line-through text-red-400" : "text-[var(--color-primary)] opacity-75"}`}
                      >
                        {inv.order_number}
                      </Link>
                      {inv.contractor_name && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                          {inv.contractor_name}
                        </p>
                      )}
                      {inv.submitted_by_name && (
                        <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                          Dodał(a): {inv.submitted_by_name}
                        </p>
                      )}
                      <p
                        className={`text-sm font-semibold mt-1 tabular-nums ${isWithdrawn ? "line-through text-red-400" : "text-gray-900 dark:text-gray-100"}`}
                      >
                        {formatAmount(displayAmount(inv))} PLN
                        <span className="ml-1 text-[10px] font-normal text-gray-400 dark:text-gray-500">
                          {isMandateInvoice(inv) ? "brutto" : "netto"}
                        </span>
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <Badge color={statusColor[inv.status] ?? "gray"}>
                        {statusLabel[inv.status] ?? inv.status}
                      </Badge>
                      {!isWithdrawn && (
                        <Badge
                          color={
                            inv.booking_status === "booked" ? "green" : "gray"
                          }
                        >
                          {inv.booking_status === "booked"
                            ? "Zaks."
                            : "Niezaks."}
                        </Badge>
                      )}
                      {!isWithdrawn && (
                        <Badge
                          color={paymentColor[inv.payment_status] ?? "gray"}
                        >
                          {paymentLabel[inv.payment_status] ??
                            inv.payment_status}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {!isWithdrawn && getActionBadges(inv, user?.role).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {getActionBadges(inv, user?.role).map((b) => (
                        <span key={b.label} className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${b.className}`}>
                          {b.label}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-2">
                    {!isWithdrawn && (
                      <>
                        <button
                          onClick={() => openModal(inv, "status")}
                          className="text-xs text-[var(--color-primary)] hover:underline"
                        >
                          Zmień status
                        </button>
                        <button
                          onClick={() => openModal(inv, "booking")}
                          className="text-xs text-[var(--color-primary)] hover:underline"
                        >
                          Zaksięgowanie
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => setHistoryInvoiceId(inv.id)}
                      title="Historia"
                      className="text-gray-400 hover:text-[var(--color-primary)] transition-colors"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
            {sorted.length === 0 && (
              <p className="px-4 py-8 text-center text-gray-400 dark:text-gray-500 text-sm">
                {filtersActive ? "Brak faktur spełniających filtry" : "Brak faktur"}
              </p>
            )}
          </div>
          {/* Desktop table */}
          <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs min-w-[900px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th
                    className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => handleSort("invoice_number")}
                  >
                    Nr faktury <SortIcon col="invoice_number" />
                  </th>
                  <th
                    className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => handleSort("order_number")}
                  >
                    Zamówienie <SortIcon col="order_number" />
                  </th>
                  <th
                    className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => handleSort("contractor_name")}
                  >
                    Kontrahent <SortIcon col="contractor_name" />
                  </th>
                  <th
                    className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => handleSort("submitted_by_name")}
                  >
                    Autor <SortIcon col="submitted_by_name" />
                  </th>
                  <th
                    className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => handleSort("amount_gross")}
                  >
                    Kwota <SortIcon col="amount_gross" />
                  </th>
                  <th
                    className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => handleSort("status")}
                  >
                    Status <SortIcon col="status" />
                  </th>
                  <th
                    className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => handleSort("booking_status")}
                  >
                    Zaksięgowanie <SortIcon col="booking_status" />
                  </th>
                  <th
                    className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
                    onClick={() => handleSort("payment_status")}
                  >
                    Opłacenie <SortIcon col="payment_status" />
                  </th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    Do zrobienia
                  </th>
                  <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                    Akcje
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sorted.map((inv, i) => {
                  const isWithdrawn = inv.status === "withdrawn";
                  return (
                    <tr
                      key={inv.id}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${isWithdrawn ? "opacity-70 bg-red-50" : ""}`}
                    >
                      <td className="px-4 py-3 font-medium">
                        <Link
                          to={`/invoices/${inv.id}`}
                          className={`hover:underline ${isWithdrawn ? "line-through text-red-400" : "text-[var(--color-primary)]"}`}
                        >
                          {inv.invoice_number}
                        </Link>{" "}
                        <InvoiceAiBadge analysis={inv.ai_analysis} />
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/orders/${inv.order_id}`}
                          className={`hover:underline ${isWithdrawn ? "line-through text-red-400" : "text-[var(--color-primary)] opacity-75"}`}
                        >
                          {inv.order_number}
                        </Link>
                      </td>
                      <td className={`px-4 py-3 ${isWithdrawn ? "line-through text-red-400" : "text-gray-700 dark:text-gray-300"}`}>
                        {inv.contractor_name || "—"}
                      </td>
                      <td className={`px-4 py-3 ${isWithdrawn ? "line-through text-red-400" : "text-gray-700 dark:text-gray-300"}`}>
                        {inv.submitted_by_name || "—"}
                      </td>
                      <td
                        className={`px-4 py-3 text-right font-medium whitespace-nowrap tabular-nums ${isWithdrawn ? "line-through text-red-400" : ""}`}
                      >
                        {/* Calculator cell id is the invoice's own id, so a selection sticks
                            to that invoice when a filter or a sort moves the row. */}
                        <CalcCell
                          id={`fv:${inv.id}:0`}
                          tableId="invoices"
                          index={i}
                          col={0}
                          value={Number(parseFloat(displayAmount(inv)).toFixed(2))}
                          currency="PLN"
                        >
                          {formatAmount(displayAmount(inv))} PLN
                          <span className="ml-1 text-[10px] font-normal text-gray-400 dark:text-gray-500">
                            {isMandateInvoice(inv) ? "brutto" : "netto"}
                          </span>
                        </CalcCell>
                      </td>
                      <td className="px-4 py-3">
                        <Badge color={statusColor[inv.status] ?? "gray"}>
                          {statusLabel[inv.status] ?? inv.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {!isWithdrawn && (
                          <Badge
                            color={
                              inv.booking_status === "booked" ? "green" : "gray"
                            }
                          >
                            {inv.booking_status === "booked"
                              ? "Zaksięgowana"
                              : "Niezaksięgowana"}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {!isWithdrawn && (
                          <Badge
                            color={paymentColor[inv.payment_status] ?? "gray"}
                          >
                            {paymentLabel[inv.payment_status] ??
                              inv.payment_status}
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {!isWithdrawn && (
                          <div className="flex flex-wrap gap-1">
                            {getActionBadges(inv, user?.role).map((b) => (
                              <span key={b.label} className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${b.className}`}>
                                {b.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {!isWithdrawn && (
                            <>
                              <button
                                onClick={() => openModal(inv, "status")}
                                className="text-xs text-[var(--color-primary)] hover:underline"
                              >
                                Status
                              </button>
                              <span className="text-gray-300">|</span>
                              <button
                                onClick={() => openModal(inv, "booking")}
                                className="text-xs text-[var(--color-primary)] hover:underline"
                              >
                                Księgowanie
                              </button>
                              <span className="text-gray-300">|</span>
                            </>
                          )}
                          <button
                            onClick={() => setHistoryInvoiceId(inv.id)}
                            title="Historia"
                            className="text-gray-400 hover:text-[var(--color-primary)] transition-colors"
                          >
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {sorted.length === 0 && (
                  <tr>
                    <td
                      colSpan={10}
                      className="px-4 py-8 text-center text-gray-400 dark:text-gray-500"
                    >
                      {filtersActive ? "Brak faktur spełniających filtry" : "Brak faktur"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Monthly stats table */}
          {monthlyStats.length > 0 && (
            <div className="mt-10">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                Zestawienie miesięczne faktur P-GK
                <span className="ml-2 text-xs font-normal normal-case text-gray-400">
                  wg aktualnych filtrów, po dacie przesłania
                </span>
              </h2>

              {/* Mobile: card list */}
              <div className="sm:hidden space-y-2">
                {monthlyStats.map((row) => (
                  <div key={row.key} className="bg-white dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                    <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 capitalize mb-2">
                      {row.label}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Przyjęte</div>
                        <div className="text-xs font-semibold text-blue-700 dark:text-blue-400 tabular-nums">
                          {row.accepted > 0 ? formatAmount(row.accepted.toFixed(2)) : <span className="text-gray-300">—</span>}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Czekające</div>
                        <div className="text-xs font-semibold text-amber-700 dark:text-amber-400 tabular-nums">
                          {row.pending > 0 ? formatAmount(row.pending.toFixed(2)) : <span className="text-gray-300">—</span>}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Zapłacono</div>
                        <div className="text-xs font-semibold text-green-700 dark:text-green-400 tabular-nums">
                          {row.paid > 0 ? formatAmount(row.paid.toFixed(2)) : <span className="text-gray-300">—</span>}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Pozostało</div>
                        <div className="text-xs font-semibold text-[var(--color-primary)] tabular-nums">
                          {row.outstanding > 0 ? formatAmount(row.outstanding.toFixed(2)) : <span className="text-gray-300">—</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {/* Razem card */}
                <div className="bg-gray-50 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg p-3">
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                    Razem
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Przyjęte</div>
                      <div className="text-xs font-bold text-blue-800 dark:text-blue-300 tabular-nums">
                        {formatAmount(monthlyTotals.accepted.toFixed(2))}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Czekające</div>
                      <div className="text-xs font-bold text-amber-800 dark:text-amber-300 tabular-nums">
                        {formatAmount(monthlyTotals.pending.toFixed(2))}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Zapłacono</div>
                      <div className="text-xs font-bold text-green-800 dark:text-green-300 tabular-nums">
                        {formatAmount(monthlyTotals.paid.toFixed(2))}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Pozostało</div>
                      <div className="text-xs font-bold text-[var(--color-primary)] tabular-nums">
                        {formatAmount(monthlyTotals.outstanding.toFixed(2))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop: table */}
              <div className="hidden sm:block overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left px-4 py-3 font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider">
                        Miesiąc
                      </th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider">
                        Przyjęte (netto)
                      </th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider">
                        Czekające na decyzję
                      </th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider">
                        Zapłacono (netto)
                      </th>
                      <th className="text-right px-4 py-3 font-semibold text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider">
                        Pozostało (netto)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                    {monthlyStats.map((row, i) => (
                      <tr
                        key={row.key}
                        className={`transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                          i % 2 === 0
                            ? "bg-white dark:bg-gray-900/20"
                            : "bg-gray-50/50 dark:bg-gray-800/20"
                        }`}
                      >
                        <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300 font-medium capitalize whitespace-nowrap">
                          {row.label}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                          <CalcCell id={`monthly:${row.key}:0`} tableId="monthly" index={i} col={0} value={row.accepted} currency="PLN">
                            {row.accepted > 0 ? (
                              <span className="text-sm font-semibold text-blue-700 dark:text-blue-400">
                                {formatAmount(row.accepted.toFixed(2))}{" "}
                                <span className="text-xs font-normal text-gray-400">PLN</span>
                              </span>
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                            )}
                          </CalcCell>
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                          <CalcCell id={`monthly:${row.key}:1`} tableId="monthly" index={i} col={1} value={row.pending} currency="PLN">
                            {row.pending > 0 ? (
                              <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">
                                {formatAmount(row.pending.toFixed(2))}{" "}
                                <span className="text-xs font-normal text-gray-400">PLN</span>
                              </span>
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                            )}
                          </CalcCell>
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                          <CalcCell id={`monthly:${row.key}:2`} tableId="monthly" index={i} col={2} value={row.paid} currency="PLN">
                            {row.paid > 0 ? (
                              <span className="text-sm font-semibold text-green-700 dark:text-green-400">
                                {formatAmount(row.paid.toFixed(2))}{" "}
                                <span className="text-xs font-normal text-gray-400">PLN</span>
                              </span>
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                            )}
                          </CalcCell>
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                          <CalcCell id={`monthly:${row.key}:3`} tableId="monthly" index={i} col={3} value={row.outstanding} currency="PLN">
                            {row.outstanding > 0 ? (
                              <span className="text-sm font-semibold text-[var(--color-primary)]">
                                {formatAmount(row.outstanding.toFixed(2))}{" "}
                                <span className="text-xs font-normal text-gray-400">PLN</span>
                              </span>
                            ) : (
                              <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                            )}
                          </CalcCell>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                      <td className="px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Razem
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                        <CalcCell id="monthly:total:0" tableId="monthly" index={monthlyStats.length} col={0} value={monthlyTotals.accepted} currency="PLN">
                          <span className="text-sm font-bold text-blue-800 dark:text-blue-300">
                            {formatAmount(monthlyTotals.accepted.toFixed(2))}{" "}
                            <span className="text-xs font-normal text-gray-400">PLN</span>
                          </span>
                        </CalcCell>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                        <CalcCell id="monthly:total:1" tableId="monthly" index={monthlyStats.length} col={1} value={monthlyTotals.pending} currency="PLN">
                          <span className="text-sm font-bold text-amber-800 dark:text-amber-300">
                            {formatAmount(monthlyTotals.pending.toFixed(2))}{" "}
                            <span className="text-xs font-normal text-gray-400">PLN</span>
                          </span>
                        </CalcCell>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                        <CalcCell id="monthly:total:2" tableId="monthly" index={monthlyStats.length} col={2} value={monthlyTotals.paid} currency="PLN">
                          <span className="text-sm font-bold text-green-800 dark:text-green-300">
                            {formatAmount(monthlyTotals.paid.toFixed(2))}{" "}
                            <span className="text-xs font-normal text-gray-400">PLN</span>
                          </span>
                        </CalcCell>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums whitespace-nowrap">
                        <CalcCell id="monthly:total:3" tableId="monthly" index={monthlyStats.length} col={3} value={monthlyTotals.outstanding} currency="PLN">
                          <span className="text-sm font-bold text-[var(--color-primary)]">
                            {formatAmount(monthlyTotals.outstanding.toFixed(2))}{" "}
                            <span className="text-xs font-normal text-gray-400">PLN</span>
                          </span>
                        </CalcCell>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="mt-2 text-[11px] text-gray-400 dark:text-gray-500">
                „Przyjęte” to faktury ze statusem Przyjęta, „Czekające na decyzję” — Przesłane.
                Nieprzyjęte i wycofane nie wchodzą do żadnej kolumny. Wpłaty są rejestrowane brutto,
                więc część netto liczona jest proporcjonalnie — tak jak w kafelkach u góry.
              </p>
            </div>
          )}
        </>
      )}

      <Modal
        open={!!historyInvoiceId}
        onClose={() => setHistoryInvoiceId(null)}
        title={`Historia — ${historyInvoice?.invoice_number ?? ""}`}
        size="sm"
      >
        {(historyLoading || historyFetching) ? (
          <div className="py-8 text-center text-gray-400 text-sm">Ładowanie...</div>
        ) : historyError ? (
          <div className="py-8 text-center text-red-400 text-sm">Błąd podczas ładowania historii.</div>
        ) : !historyData?.length ? (
          <div className="py-8 text-center text-gray-400 text-sm">Brak historii dla tej faktury.</div>
        ) : (
          <ol className="relative border-l border-gray-200 dark:border-gray-700 ml-2">
            {historyData.map((ev) => (
              <li key={ev.id} className="mb-5 ml-4">
                <span className={`absolute -left-1.5 mt-1 w-3 h-3 rounded-full ${eventTypeColor[ev.event_type] ?? "bg-gray-400"}`} />
                <p className="text-xs text-gray-400 dark:text-gray-500 mb-0.5">{formatTs(ev.timestamp)}</p>
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {eventTypeLabel[ev.event_type] ?? ev.event_type}
                </p>
                {ev.event_type === "submitted" && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    przez {ev.actor_name}
                  </p>
                )}
                {ev.event_type === "status_changed" && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {eventValueLabel[ev.old_value] ?? ev.old_value} → <span className="font-medium">{eventValueLabel[ev.new_value] ?? ev.new_value}</span>
                    {" · "}{ev.actor_name}
                  </p>
                )}
                {ev.event_type === "booking_changed" && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {eventValueLabel[ev.old_value] ?? ev.old_value} → <span className="font-medium">{eventValueLabel[ev.new_value] ?? ev.new_value}</span>
                    {" · "}{ev.actor_name}
                  </p>
                )}
                {(ev.event_type === "payment_added" || ev.event_type === "payment_deleted") && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {ev.event_type === "payment_added"
                      ? <>{formatAmount(ev.new_value)} PLN{ev.comment ? ` · ${ev.comment}` : ""}</>
                      : <>{formatAmount(ev.old_value)} PLN{ev.comment ? ` · ${ev.comment}` : ""}</>
                    }
                    {" · "}{ev.actor_name}
                  </p>
                )}
                {ev.comment && ev.event_type === "status_changed" && ev.comment.trim() && (
                  <p className="text-xs text-gray-400 italic mt-0.5">„{ev.comment}"</p>
                )}
              </li>
            ))}
          </ol>
        )}
      </Modal>

      <Modal
        open={!!modalType}
        onClose={() => setModalType(null)}
        title={
          modalType === "status"
            ? "Zmień status faktury"
            : "Zmień status zaksięgowania"
        }
        size="sm"
      >
        {modalType === "status" && (
          <div className="flex flex-col gap-4">
            <Select
              label="Status"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              options={[
                { value: "submitted", label: "Przesłana" },
                { value: "accepted", label: "Przyjęta" },
                { value: "rejected", label: "Nieprzyjęta" },
              ]}
            />
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Komentarz
              </label>
              <textarea
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                rows={2}
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
              />
            </div>
            <Button
              loading={mutation.isPending}
              onClick={() =>
                mutation.mutate({
                  action: "update_status",
                  payload: {
                    status: form.status,
                    status_comment: form.comment,
                  },
                })
              }
            >
              Zapisz
            </Button>
          </div>
        )}
        {modalType === "booking" && (
          <div className="flex flex-col gap-4">
            <Select
              label="Status zaksięgowania"
              value={form.booking_status}
              onChange={(e) =>
                setForm({ ...form, booking_status: e.target.value })
              }
              options={[
                { value: "not_booked", label: "Niezaksięgowana" },
                { value: "booked", label: "Zaksięgowana" },
              ]}
            />
            <Button
              loading={mutation.isPending}
              onClick={() =>
                mutation.mutate({
                  action: "update_booking",
                  payload: { booking_status: form.booking_status },
                })
              }
            >
              Zapisz
            </Button>
          </div>
        )}
      </Modal>
    </div>
    </SheetCalcProvider>
  );
}
